import { PartDef, circleArea, axialNodes } from './part'
import { vec3 } from '../math/vec3'

const R = 0.625 // raggio standard delle parti da 1.25 m di diametro

/**
 * Temperature di distruzione realistiche. L'alluminio fonde a 933 K: una
 * struttura che regge 2400 K non esiste, e con quel valore nessun rientro
 * sarebbe mai pericoloso e lo scudo termico sarebbe un ornamento.
 */
const T_STRUTTURA = 1150      // lega di alluminio con un po' di isolante
const T_MOTORE = 1500         // le camere di combustione stanno gia caldissime
const T_SCUDO = 3300          // ablativo: e fatto per stare li

/**
 * Pendenza della curva di portanza corretta per ALLUNGAMENTO FINITO:
 *   a = a₀ / (1 + a₀/(π·AR·e))     con a₀ = 2π e fattore di Oswald e ≈ 0.9
 *
 * Il 2π della teoria del profilo sottile vale per un'ala di allungamento
 * infinito. Una pinna tozza (AR sotto 1) ne sviluppa meno di un terzo: usare
 * 2π le renderebbe cosi potenti da inchiodare il razzo alla prograde, e il
 * pilota non riuscirebbe piu a impostare il giro di gravita col gimbal.
 */
const pendenzaPortanza = (allungamento: number, oswald = 0.9): number => {
  const a0 = 2 * Math.PI
  return a0 / (1 + a0 / (Math.PI * allungamento * oswald))
}

const defs: PartDef[] = [
  {
    id: 'capsule-mk1', name: 'Capsula Mk1', category: 'command',
    dryMass: 800, shape: { kind: 'cone', radius: R, height: 1.2 },
    nodes: axialNodes(0.6, 1.25), cd: 0.3, refArea: circleArea(R), maxTemp: T_STRUTTURA,
  },
  {
    id: 'nose-cone', name: 'Ogiva', category: 'aero',
    dryMass: 80, shape: { kind: 'cone', radius: R, height: 1.0 },
    nodes: [{ id: 'bottom', position: vec3(0, -0.5, 0), direction: vec3(0, -1, 0), size: 1.25, kind: 'axial' }],
    cd: 0.15, refArea: circleArea(R), maxTemp: T_STRUTTURA,
  },
  {
    id: 'tank-small', name: 'Serbatoio piccolo', category: 'tank',
    dryMass: 125, shape: { kind: 'cylinder', radius: R, height: 1.875 },
    nodes: axialNodes(0.9375, 1.25), cd: 0.2, refArea: circleArea(R), maxTemp: T_STRUTTURA,
    tank: { propellantMass: 1125 },
  },
  {
    id: 'tank-medium', name: 'Serbatoio medio', category: 'tank',
    dryMass: 250, shape: { kind: 'cylinder', radius: R, height: 3.75 },
    nodes: axialNodes(1.875, 1.25), cd: 0.2, refArea: circleArea(R), maxTemp: T_STRUTTURA,
    tank: { propellantMass: 2250 },
  },
  {
    id: 'tank-large', name: 'Serbatoio grande', category: 'tank',
    dryMass: 500, shape: { kind: 'cylinder', radius: R, height: 7.5 },
    nodes: axialNodes(3.75, 1.25), cd: 0.2, refArea: circleArea(R), maxTemp: T_STRUTTURA,
    tank: { propellantMass: 4500 },
  },
  {
    id: 'engine-vespa', name: 'Vespa', category: 'engine',
    dryMass: 300, shape: { kind: 'cylinder', radius: R * 0.7, height: 1.2 },
    nodes: [{ id: 'top', position: vec3(0, 0.6, 0), direction: vec3(0, 1, 0), size: 1.25, kind: 'axial' }],
    cd: 0.4, refArea: circleArea(R * 0.7), maxTemp: T_MOTORE,
    engine: { thrustVac: 60_000, thrustSl: 45_000, ispVac: 320, ispSl: 290, gimbalRange: (3 * Math.PI) / 180, throttleable: true },
  },
  {
    id: 'engine-falco', name: 'Falco', category: 'engine',
    dryMass: 1500, shape: { kind: 'cylinder', radius: R, height: 2.0 },
    nodes: [{ id: 'top', position: vec3(0, 1.0, 0), direction: vec3(0, 1, 0), size: 1.25, kind: 'axial' }],
    cd: 0.4, refArea: circleArea(R), maxTemp: T_MOTORE,
    engine: { thrustVac: 240_000, thrustSl: 205_000, ispVac: 320, ispSl: 290, gimbalRange: (5 * Math.PI) / 180, throttleable: true },
  },
  {
    id: 'engine-titano', name: 'Titano', category: 'engine',
    dryMass: 6000, shape: { kind: 'cylinder', radius: 1.25, height: 3.0 },
    nodes: [{ id: 'top', position: vec3(0, 1.5, 0), direction: vec3(0, 1, 0), size: 2.5, kind: 'axial' }],
    cd: 0.4, refArea: circleArea(1.25), maxTemp: T_MOTORE,
    engine: { thrustVac: 1_500_000, thrustSl: 1_300_000, ispVac: 300, ispSl: 265, gimbalRange: (2 * Math.PI) / 180, throttleable: true },
  },
  {
    id: 'engine-vuoto', name: 'Vuoto', category: 'engine',
    dryMass: 500, shape: { kind: 'cylinder', radius: R, height: 1.6 },
    nodes: [{ id: 'top', position: vec3(0, 0.8, 0), direction: vec3(0, 1, 0), size: 1.25, kind: 'axial' }],
    cd: 0.4, refArea: circleArea(R), maxTemp: T_MOTORE,
    engine: { thrustVac: 60_000, thrustSl: 15_000, ispVac: 350, ispSl: 180, gimbalRange: (4 * Math.PI) / 180, throttleable: true },
  },
  {
    id: 'decoupler-axial', name: 'Disaccoppiatore', category: 'decoupler',
    dryMass: 50, shape: { kind: 'cylinder', radius: R, height: 0.3 },
    nodes: axialNodes(0.15, 1.25), cd: 0.2, refArea: circleArea(R), maxTemp: T_STRUTTURA,
    decoupler: { ejectionForce: 2500, crossfeed: false },
  },
  {
    id: 'fin-fixed', name: 'Aletta fissa', category: 'aero',
    dryMass: 50, shape: { kind: 'box', x: 0.05, y: 1.0, z: 0.8 },
    // la radice sta sul lato −Z: agganciandola al fianco di un serbatoio, la
    // pala si estende verso l'esterno invece che di traverso
    nodes: [{ id: 'root', position: vec3(0, 0, -0.4), direction: vec3(0, 0, -1), size: 0, kind: 'radial' }],
    cd: 0.1, refArea: 0.1, maxTemp: T_STRUTTURA, crossflowFactor: 0,
    aeroSurface: { liftSlope: pendenzaPortanza(0.8), area: 0.8, stallAngle: (18 * Math.PI) / 180, normal: vec3(1, 0, 0), controllable: false },
  },
  {
    id: 'leg-small', name: 'Gamba di atterraggio', category: 'leg',
    dryMass: 70, shape: { kind: 'box', x: 0.16, y: 1.6, z: 0.9 },
    // la radice sta sul lato −Z, come le alette: il piede si apre verso fuori
    nodes: [{ id: 'root', position: vec3(0, 0.6, -0.45), direction: vec3(0, 0, -1), size: 0, kind: 'radial' }],
    cd: 0.2, refArea: 0.15, maxTemp: T_STRUTTURA, crossflowFactor: 0.6,
    // il piede: e questo il punto che tocca terra
    footPoint: vec3(0, -0.8, 0.42),
  },
  {
    id: 'parachute-main', name: 'Paracadute', category: 'parachute',
    dryMass: 90, shape: { kind: 'cylinder', radius: R * 0.55, height: 0.7 },
    nodes: [{ id: 'bottom', position: vec3(0, -0.35, 0), direction: vec3(0, -1, 0), size: 1.25, kind: 'axial' }],
    cd: 0.25, refArea: circleArea(R * 0.55), maxTemp: 800,
    parachute: { areaAperto: 42, areaParziale: 5, pressioneStrappo: 12_000, quotaApertura: 1200 },
  },
  {
    id: 'heat-shield', name: 'Scudo termico', category: 'heatshield',
    dryMass: 220, shape: { kind: 'cone', radius: R * 1.2, height: 0.45 },
    nodes: axialNodes(0.225, 1.25), cd: 0.9, refArea: circleArea(R * 1.2), maxTemp: T_SCUDO,
    heatShield: { ablativo: 180, capacita: 1.2e6 },
  },
  {
    id: 'fin-control', name: 'Aletta mobile', category: 'aero',
    dryMass: 60, shape: { kind: 'box', x: 0.05, y: 0.8, z: 0.7 },
    nodes: [{ id: 'root', position: vec3(0, 0, -0.35), direction: vec3(0, 0, -1), size: 0, kind: 'radial' }],
    cd: 0.1, refArea: 0.1, maxTemp: T_STRUTTURA, crossflowFactor: 0,
    aeroSurface: { liftSlope: pendenzaPortanza(0.7), area: 0.7, stallAngle: (18 * Math.PI) / 180, normal: vec3(1, 0, 0), controllable: true },
  },
]

export type PartCatalog = Readonly<Record<string, PartDef>>

export const CATALOG: PartCatalog = Object.freeze(
  Object.fromEntries(defs.map((d) => [d.id, d])),
)

export function partDef(catalog: PartCatalog, id: string): PartDef {
  const d = catalog[id]
  if (!d) throw new Error(`Parte sconosciuta nel catalogo: ${id}`)
  return d
}
